import {
  ComponentType,
  PropsWithChildren,
  ReactNode,
  createContext,
  createRef,
  useContext,
  useRef,
} from 'react';
import * as reach from '@reach/tabs';

export * from '@reach/tabs';

export interface TabData<T = string> {
  readonly id: T;
  readonly label: ReactNode;
  readonly content: ReactNode;
  readonly disabled?: boolean;
}

type BaseDataTabsProps = Omit<reach.TabsProps, 'children'>;

export interface DataTabsProps extends BaseDataTabsProps {
  readonly data: readonly TabData[];
  readonly containerComponent?: ComponentType<reach.TabsProps>;
  readonly tabComponent?: ComponentType<reach.TabProps>;
  readonly tabListComponent?: ComponentType<reach.TabListProps>;
  readonly tabPanelsComponent?: ComponentType<reach.TabPanelsProps>;
  readonly tabPanelComponent?: ComponentType<
    reach.TabPanelProps & React.RefAttributes<HTMLDivElement>
  >;
}

export function DataTabs(props: DataTabsProps) {
  const {
    data,
    containerComponent: TabsContainer = reach.Tabs,
    tabComponent: Tab = reach.Tab,
    tabListComponent: TabList = reach.TabList,
    tabPanelsComponent: TabPanels = reach.TabPanels,
    tabPanelComponent: TabPanel = reach.TabPanel,
    ...rest
  } = props;

  return (
    <TabsContainer {...rest}>
      <TabList>
        {data.map((tab, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Tab key={index} disabled={tab.disabled}>
            {tab.label}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {data.map((tab, index) => (
          <TabPanelWithRefContext
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            component={TabPanel}
          >
            {tab.content}
          </TabPanelWithRefContext>
        ))}
      </TabPanels>
    </TabsContainer>
  );
}

const tabPanelRefContext = createContext(createRef<HTMLDivElement>());

interface TabPanelWithRefContextProps
  extends PropsWithChildren<{
    readonly component: ComponentType<
      reach.TabPanelProps & React.RefAttributes<HTMLDivElement>
    >;
  }> {}

function TabPanelWithRefContext({
  component: TabPanel,
  children,
}: TabPanelWithRefContextProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  return (
    <TabPanel ref={panelRef}>
      <tabPanelRefContext.Provider value={panelRef}>
        {children}
      </tabPanelRefContext.Provider>
    </TabPanel>
  );
}

export function useTabPanelRef() {
  return useContext(tabPanelRefContext);
}
